import { Grid, Stack, TextField, Autocomplete, Card, Typography, InputAdornment } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import axios from "axios";
import { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from "react";
import { PropertyInfo } from "../orderEntry/types/Property";
import { County, State } from "./types";
import PropertyLots from "./PropertyLots";
import PropertyParcels from "./PropertyParcels";
import _ from "lodash";

type ReviewFieldHoverProps = { onMouseEnter: () => void; onMouseLeave: () => void };

interface IProps {
  propertyInfo: PropertyInfo;
  setPropertyInfo: Dispatch<SetStateAction<PropertyInfo>>;
  county: County | null;
  setCounty: (e: any) => void;
  stateValue: State | null;
  setStateValue: (e: any) => void;
  stateError: boolean;
  cityError: boolean;
  addressError: boolean;
  sblError: boolean;
  isEditMode?: boolean;
  disabled?: boolean;
  renderReviewActions?: (fieldId: string, displayedValue: unknown) => ReactNode;
  getReviewFieldHoverProps?: (fieldId: string) => ReviewFieldHoverProps;
  getReviewFieldHighlightSx?: (fieldId: string) => SxProps<Theme>;
}

export default function NewPropertyInfo({
  propertyInfo,
  setPropertyInfo,
  county,
  setCounty,
  stateValue,
  setStateValue,
  stateError,
  cityError,
  addressError,
  sblError,
  isEditMode,
  disabled,
  renderReviewActions,
  getReviewFieldHoverProps,
  getReviewFieldHighlightSx
}: IProps) {
  const [states, setStates] = useState<State[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const lockedProperty = useRef(propertyInfo);
  const lockedState = useRef(stateValue);
  const lockedCounty = useRef(county);
  const ro = (val: any) => Boolean(disabled) || (isEditMode && Boolean(val));
  const unitInputRef = useRef<HTMLInputElement | null>(null);

  const getStates = async () => {
    const { data } = await axios.get("/api/blacklist/getstates");
    setStates(data);
  };
  const handleStateChange = (selectedState: any) => {
    if (selectedState) {
      setCounties(selectedState.counties || []);
      setStateValue(selectedState);
      setCounty({});
    } else {
      setCounties([]);
      setStateValue({});
      setCounty({});
    }
  };

  useEffect(() => {
    getStates();
  }, []);

  useEffect(() => {
    setCounties(stateValue?.counties || []);
  }, [stateValue]);

  return (
    <Card sx={{ p: 4, maxWidth: 860, marginTop: 4 }}>
      <Typography variant="h5">Address</Typography>
      <Stack direction="row" justifyContent="space-between" marginTop="40px" spacing={2}>
        <Grid item md={10} xs={12}>
          <TextField
            style={{ width: "100%" }}
            label="Address"
            value={propertyInfo?.address || ""}
            {...getReviewFieldHoverProps?.("propertyAddress")}
            sx={getReviewFieldHighlightSx?.("propertyAddress")}
            onChange={(e) => {
              setPropertyInfo({ ...propertyInfo, address: e.target.value });
            }}
            error={addressError}
            disabled={ro(lockedProperty.current?.address)}
            InputProps={{
              endAdornment: renderReviewActions ? (
                <InputAdornment position="end">{renderReviewActions("propertyAddress", propertyInfo?.address)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
        <Grid item md={2} xs={12}>
          <TextField
            label="Unit/Suite/Apt #"
            value={propertyInfo?.aptNo || ""}
            {...getReviewFieldHoverProps?.("propertyUnit")}
            sx={getReviewFieldHighlightSx?.("propertyUnit")}
            onChange={(e) => {
              setPropertyInfo({ ...propertyInfo, aptNo: e.target.value });
            }}
            inputRef={unitInputRef}
            disabled={ro(lockedProperty.current?.aptNo)}
            InputProps={{
              endAdornment: renderReviewActions ? (
                <InputAdornment position="end">{renderReviewActions("propertyUnit", propertyInfo?.aptNo)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
      </Stack>
      <Stack direction="row" justifyContent="space-between" marginTop="24px" spacing={3}>
        <Grid item md={10} xs={12}>
          <TextField
            label="City"
            value={propertyInfo?.city || ""}
            {...getReviewFieldHoverProps?.("propertyCity")}
            sx={getReviewFieldHighlightSx?.("propertyCity")}
            onChange={(e) => setPropertyInfo({ ...propertyInfo, city: e.target.value })}
            fullWidth
            error={cityError}
            required
            disabled={ro(lockedProperty.current?.city)}
            InputProps={{
              endAdornment: renderReviewActions ? (
                <InputAdornment position="end">{renderReviewActions("propertyCity", propertyInfo?.city)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <Autocomplete
            options={states || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                error={stateError}
                required
                {...getReviewFieldHoverProps?.("propertyStateId")}
                sx={getReviewFieldHighlightSx?.("propertyStateId")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {renderReviewActions && (
                        <InputAdornment position="end">{renderReviewActions("propertyStateId", stateValue?.name || "")}</InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            getOptionLabel={(option) => option.name || stateValue?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(e, value) => handleStateChange(value)}
            value={stateValue || null}
            disabled={ro(lockedState.current?.id)}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <TextField
            label="Zip Code"
            value={propertyInfo?.zipCode || ""}
            {...getReviewFieldHoverProps?.("propertyZip")}
            sx={getReviewFieldHighlightSx?.("propertyZip")}
            onChange={(e) => setPropertyInfo({ ...propertyInfo, zipCode: e.target.value })}
            disabled={ro(lockedProperty.current?.zipCode)}
            InputProps={{
              endAdornment: renderReviewActions ? (
                <InputAdornment position="end">{renderReviewActions("propertyZip", propertyInfo?.zipCode)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
      </Stack>
      <Stack direction="row" marginTop="30px" justifyContent="space-between" spacing={4}>
        <Autocomplete
          options={counties || []}
          renderInput={(params) => (
            <TextField
              {...params}
              label="County"
              fullWidth
              {...getReviewFieldHoverProps?.("propertyCountyId")}
              sx={getReviewFieldHighlightSx?.("propertyCountyId")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {renderReviewActions && (
                      <InputAdornment position="end">{renderReviewActions("propertyCountyId", county?.name || "")}</InputAdornment>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          getOptionLabel={(option) => option.name || county?.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(e, val) => setCounty(val)}
          value={county || null}
          sx={{ width: "50%" }}
          disabled={ro(lockedCounty.current?.id)}
        />

        <PropertyParcels
          parcelIds={propertyInfo.parcelIds}
          setParcelIds={(updatedParcels) => {
            setPropertyInfo({
              ...propertyInfo,
              parcelIds: _.isEmpty(updatedParcels) ? [""] : updatedParcels
            });
          }}
          lockedParcelIds={lockedProperty.current?.parcelIds}
          isEditMode={isEditMode}
          disabled={disabled}
          renderReviewActions={renderReviewActions}
          getReviewFieldHoverProps={getReviewFieldHoverProps}
          getReviewFieldHighlightSx={getReviewFieldHighlightSx}
        />
      </Stack>
      <Stack direction="row" marginTop="30px" justifyContent="space-between" spacing={2}>
        <TextField
          label="Section/Borough"
          value={propertyInfo?.section || propertyInfo?.borough}
          {...getReviewFieldHoverProps?.("propertySection")}
          sx={getReviewFieldHighlightSx?.("propertySection")}
          onChange={(e) => setPropertyInfo({ ...propertyInfo, section: e.target.value })}
          disabled={ro(lockedProperty.current?.section || lockedProperty.current?.borough)}
          InputProps={{
            endAdornment: renderReviewActions ? (
              <InputAdornment position="end">{renderReviewActions("propertySection", propertyInfo?.section || propertyInfo?.borough)}</InputAdornment>
            ) : undefined
          }}
        />
        <TextField
          label="Block"
          value={propertyInfo?.block}
          {...getReviewFieldHoverProps?.("propertyBlock")}
          sx={getReviewFieldHighlightSx?.("propertyBlock")}
          onChange={(e) => setPropertyInfo({ ...propertyInfo, block: e.target.value })}
          error={sblError}
          disabled={ro(lockedProperty.current?.block)}
          InputProps={{
            endAdornment: renderReviewActions ? (
              <InputAdornment position="end">{renderReviewActions("propertyBlock", propertyInfo?.block)}</InputAdornment>
            ) : undefined
          }}
        />
        <PropertyLots
          lots={propertyInfo.lots}
          sblError={sblError}
          setLots={(updatedLots) => {
            setPropertyInfo({
              ...propertyInfo,
              lots: _.isEmpty(updatedLots) ? [""] : updatedLots
            });
          }}
          lockedLots={lockedProperty.current?.lots}
          isEditMode={isEditMode}
          disabled={disabled}
          renderReviewActions={renderReviewActions}
          getReviewFieldHoverProps={getReviewFieldHoverProps}
          getReviewFieldHighlightSx={getReviewFieldHighlightSx}
        />
      </Stack>
      <Stack direction="row" marginTop="30px" justifyContent="space-between" spacing={2}>
        <TextField
          label="Subdivision/Condo/PUD/Addition"
          value={propertyInfo?.condoNumber}
          {...getReviewFieldHoverProps?.("propertySubdivision")}
          sx={getReviewFieldHighlightSx?.("propertySubdivision")}
          onChange={(e) => setPropertyInfo({ ...propertyInfo, condoNumber: e.target.value })}
          fullWidth
          disabled={ro(lockedProperty.current?.condoNumber)}
          InputProps={{
            endAdornment: renderReviewActions ? (
              <InputAdornment position="end">{renderReviewActions("propertySubdivision", propertyInfo?.condoNumber)}</InputAdornment>
            ) : undefined
          }}
        />
      </Stack>
    </Card>
  );
}
